/**
 * usePostScanRefresh — canonical post-scan cache invalidation.
 *
 * Watches the pipeline-latest query for a running → terminal transition,
 * then invalidates every query key that depends on scan results.
 *
 * Use this on any page that displays scan-derived data and should refresh
 * automatically when a scan completes (pipeline, dashboard, report, etc.).
 */

import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getLatestExecution } from "../api/pipeline.js";

/** All query keys that may change when a scan pipeline completes. */
function invalidateScanDependencies(
  qc: ReturnType<typeof useQueryClient>,
  websiteId: string,
  orgId: string | undefined,
) {
  // Core scan results
  void qc.invalidateQueries({ queryKey: ["score", websiteId] });
  void qc.invalidateQueries({ queryKey: ["report", websiteId] });
  void qc.invalidateQueries({ queryKey: ["report-payload", websiteId] });
  void qc.invalidateQueries({ queryKey: ["statement", websiteId] });
  void qc.invalidateQueries({ queryKey: ["statement-payload", websiteId] });

  // Website list page card queries
  void qc.invalidateQueries({ queryKey: ["website-card-score", websiteId] });
  void qc.invalidateQueries({ queryKey: ["website-card-report", websiteId] });

  // History & trends
  void qc.invalidateQueries({ queryKey: ["scores", "history", websiteId] });

  // Timeline queries
  void qc.invalidateQueries({ queryKey: ["timeline", "scores", websiteId] });
  void qc.invalidateQueries({ queryKey: ["timeline", "statements", websiteId] });
  void qc.invalidateQueries({ queryKey: ["timeline", "recurring", websiteId] });

  // Recurring / monitoring
  void qc.invalidateQueries({ queryKey: ["recurring", websiteId] });
  void qc.invalidateQueries({ queryKey: ["recurring-list", websiteId] });
  void qc.invalidateQueries({ queryKey: ["recurring-changes", websiteId] });
  void qc.invalidateQueries({ queryKey: ["recurring-alert", websiteId] });

  // Supporting data
  void qc.invalidateQueries({ queryKey: ["templates", websiteId] });
  void qc.invalidateQueries({ queryKey: ["documents", websiteId] });

  // Billing / usage (scan credit consumed) + website list metadata
  if (orgId) {
    void qc.invalidateQueries({ queryKey: ["billing-summary", orgId] });
    void qc.invalidateQueries({ queryKey: ["websites", orgId] });
  }
}

export interface PostScanRefreshResult {
  /** The latest pipeline execution, if any. */
  execution: ReturnType<typeof getLatestExecution> extends Promise<infer T>
    ? T | undefined
    : never;
  /** Whether a scan is currently running. */
  isRunning: boolean;
  /** Activate fast-poll mode (used after triggering a scan). */
  startFastPoll: () => void;
}

export function usePostScanRefresh(
  websiteId: string | undefined,
  orgId: string | undefined,
): PostScanRefreshResult {
  const qc = useQueryClient();

  // Fast-poll mode: activated when a scan is just triggered so we catch the
  // "queued → running" transition quickly instead of waiting for a slow poll.
  const [fastPoll, setFastPoll] = useState(false);

  const { data: execution } = useQuery({
    queryKey: ["pipeline-latest", websiteId],
    queryFn: () => getLatestExecution(websiteId!),
    enabled: !!websiteId,
    refetchInterval: (query) => {
      const s = query.state.data?.status;
      if (s === "running") return 5_000;
      // Fast-poll (3s) to catch the transition from no-run / terminal → running.
      if (fastPoll) return 3_000;
      return false;
    },
    retry: false,
  });

  const isRunning = execution?.status === "running";

  // Turn off fast-poll once we detect "running" (the 5s refetchInterval takes over)
  // or when the execution reaches a terminal state without ever being seen as "running"
  // (e.g. scan fails immediately because Chromium is unavailable).
  useEffect(() => {
    if (!fastPoll) return;
    if (isRunning) {
      setFastPoll(false);
      return;
    }
    const s = execution?.status;
    if (s === "success" || s === "partial" || s === "failed") {
      setFastPoll(false);
    }
  }, [isRunning, fastPoll, execution?.status]);

  // Track running → done transition and invalidate all dependent queries.
  // Reset when websiteId changes to avoid false invalidation on navigation.
  const wasRunning = useRef(false);
  const prevWebsiteId = useRef(websiteId);
  useEffect(() => {
    if (prevWebsiteId.current !== websiteId) {
      wasRunning.current = false;
      prevWebsiteId.current = websiteId;
    }
    if (isRunning) {
      wasRunning.current = true;
    } else if (wasRunning.current && websiteId) {
      wasRunning.current = false;
      invalidateScanDependencies(qc, websiteId, orgId);
    }
  }, [isRunning, qc, websiteId, orgId]);

  return { execution, isRunning, startFastPoll: () => setFastPoll(true) };
}

/**
 * Imperative invalidation for admin-initiated pipeline runs (use-pipeline.ts).
 * Matches the same canonical key set.
 */
export { invalidateScanDependencies };
